import { useCallback } from 'react'
import { emporionToast as toast } from '@admin/toast'
import { API_BASE } from './constants'
import { FetchPolicyError, fetchWithPolicy } from '../../lib/api/fetchPolicy'
import type { UseAdminAuthParams } from './useAdminAuth.types'
import { clearAuthToken } from '../../lib/api/authToken'
import { getAdminCsrfToken, setAdminCsrfToken } from '../../lib/api/csrfToken'

type LogoutParams = Pick<
  UseAdminAuthParams,
  | 't'
  | 'token'
  | 'authHeaders'
  | 'setToken'
  | 'setLoginStage'
  | 'setLoginForm'
  | 'setRequiresDeviceTrust'
  | 'clearBuilderPage'
  | 'resetProfile'
  | 'resetSecurity'
>

export const useAdminLogout = ({
  t,
  token,
  authHeaders,
  setToken,
  setLoginStage,
  setLoginForm,
  setRequiresDeviceTrust,
  clearBuilderPage,
  resetProfile,
  resetSecurity,
}: LogoutParams) =>
  useCallback(async () => {
    if (token) {
      try {
        await fetchWithPolicy({
          request: `${API_BASE}/auth/logout`,
          init: {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...authHeaders(),
              ...(getAdminCsrfToken() ? { 'x-emporion-csrf-token': getAdminCsrfToken() as string } : {}),
            },
          },
          retries: 0,
        })
      } catch (error) {
        if (!(error instanceof FetchPolicyError)) {
          throw error
        }
      }
    }

    setToken(null)
    setLoginStage('credentials')
    setLoginForm({
      username: '',
      password: '',
      otp: '',
      botProtectionToken: '',
      botProtectionProviderId: '',
      twoFactorSessionId: '',
    })
    setRequiresDeviceTrust(false)
    clearBuilderPage()
    resetProfile()
    resetSecurity()
    clearAuthToken()
    setAdminCsrfToken(null)
    toast.success(t('auth.logoutSuccess'))
  }, [
    authHeaders,
    clearBuilderPage,
    resetProfile,
    resetSecurity,
    setRequiresDeviceTrust,
    setLoginForm,
    setLoginStage,
    setToken,
    t,
    token,
  ])
